import React, { useEffect, useState } from "react"
import { ChevronLeft, ChevronRight, Plus } from "lucide-react"
import moment from "moment"
import { Modal } from "react-bootstrap"

import useBookingAvailableDates from "~/utils/useBookingAvailableDates"
import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import BookingForm from "~/components/BookingForm"
import getAppConfig from "~/utils/getAppConfig"
import { useToast } from "~/utils/Toast"
import { apiBookings } from "~/routes"
import useFetchResource from "~/hooks/useFetchResource"
import type Area from "~/types/Area"
import type Booking from "~/types/Booking"

interface DateInfo {
    date: string
    dayName: string
    monthName: string
    dayNumber: number
    isWeekend: boolean
    isPast: boolean
    isEnabled: boolean
}

interface CalendarBookingSeed {
  id: string
  prefixId: string
  name: string
  status: Booking["status"]
  clientName?: string
  totalAmount?: Booking["totalAmount"]
  totalAmountCents?: number
}

interface CalendarData {
  areas: Area[]
  bookingsByDateAndArea: Record<string, Record<string, CalendarBookingSeed>>
  dateRange: DateInfo[]
}

const BookingCalendar: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const toast = useToast()
    const { enabled } = useBookingAvailableDates()

    const [currentDate, setCurrentDate] = useState(moment().startOf('month'))
    const [calendarData, setCalendarData] = useState<CalendarData | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    const [showModal, setShowModal] = useState(false)
    const [modalMode, setModalMode] = useState<'new' | 'edit'>('new')
    const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null)
    const [prefilledDate, setPrefilledDate] = useState<string | null>(null)
    const [prefilledAreaId, setPrefilledAreaId] = useState<string | null>(null)

    const startDate = currentDate.clone().startOf('month')
    const endDate = currentDate.clone().endOf('month')

  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [isSelectedBookingLoading, setIsSelectedBookingLoading] = useState(false)

  useEffect(() => {
    if (!selectedBookingId || modalMode !== "edit") {
      setSelectedBooking(null)
      setIsSelectedBookingLoading(false)
      return
    }

    const fetchSelectedBooking = async () => {
      setIsSelectedBookingLoading(true)

      try {
        const response = await fetch(
          apiBookings.show.path({ venueSlug, id: selectedBookingId })
        )
        const data = await response.json()
        setSelectedBooking(data.booking)
      } catch (error) {
        console.error("Error loading booking:", error)
        toast("Error loading booking", "danger")
        setSelectedBooking(null)
      } finally {
        setIsSelectedBookingLoading(false)
      }
    }

    fetchSelectedBooking()
  }, [selectedBookingId, modalMode, venueSlug])

    useEffect(() => {
        fetchCalendarData()
    }, [currentDate])

    const fetchCalendarData = async () => {
        setIsLoading(true)
        try {
            const url = `/api/${venueSlug}/bookings/calendar?start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`
            const response = await fetch(url)
            const data = await response.json()

            const transformedBookings: Record<string, Record<string, CalendarBookingSeed>> = {}
            const bookingsByDate = data.bookings_by_date_and_area || {}

            Object.keys(bookingsByDate).forEach(date => {
                transformedBookings[date] = {}
                Object.keys(bookingsByDate[date]).forEach(areaId => {
                    const booking = bookingsByDate[date][areaId]
                    transformedBookings[date][areaId] = {
                      id: booking.id,
                      prefixId: booking.prefix_id,
                      name: booking.name,
                      status: booking.status,
                      clientName: booking.client_name,
                      totalAmount: booking.total_amount,
                      totalAmountCents: booking.total_amount_cents,
                    }
                })
            })

            const transformedData: CalendarData = {
                areas: (data.areas || []).map((a: any) => ({
                    ...a,
                    id: a.id,
                    prefixId: a.prefix_id,
                })),
                bookingsByDateAndArea: transformedBookings,
                dateRange: (data.date_range || []).map((d: any) => ({
                    date: d.date,
                    dayName: d.day_name,
                    monthName: d.month_name,
                    dayNumber: d.day_number,
                    isWeekend: d.is_weekend,
                    isPast: d.is_past,
                    isEnabled: d.is_enabled == null ? true : !!d.is_enabled,
                })),
            }

            setCalendarData(transformedData)
        } catch (error) {
            console.error("Error fetching calendar:", error)
            toast("Error loading calendar", "danger")
        } finally {
            setIsLoading(false)
        }
    }

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentDate(prev =>
            direction === 'prev'
                ? prev.clone().subtract(1, 'month')
                : prev.clone().add(1, 'month')
        )
    }

    const goToToday = () => {
        setCurrentDate(moment().startOf('month'))
    }

    const getBooking = (date: string, areaId: string): CalendarBookingSeed | null => {
      return calendarData?.bookingsByDateAndArea[date]?.[areaId] || null
    }

    const handleAddBooking = (date: string, areaId: string) => {
        setModalMode('new')
        setPrefilledDate(date)
        setPrefilledAreaId(areaId)
        setSelectedBookingId(null)
        setShowModal(true)
    }

    const handleEditBooking = (bookingPrefixId: string) => {
        setModalMode('edit')
        setSelectedBookingId(bookingPrefixId)
        setPrefilledDate(null)
        setPrefilledAreaId(null)
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedBookingId(null)
        setPrefilledDate(null)
        setPrefilledAreaId(null)
    }

    const handleNewBookingSubmit = (formValues: any) => {
        const data = {
            venue_slug: venueSlug,
            booking: { ...formValues, status: "confirmed" },
        }

        const req = apiBookings.create({ data })
        req.then(() => {
            toast("Booking created successfully", "success")
            handleCloseModal()
            fetchCalendarData()
        })
        req.catch((res) => {
            const errorMessage = res.body?.errors?.join(", ") || res.message || "Unknown error"
            toast(`Error creating booking. ${errorMessage}`, "danger")
        })
    }

    const handleEditBookingSubmit = (formValues: any) => {
        if (!selectedBooking) return

        const filteredBookingData = {
            name: formValues.name,
            date: formValues.date,
            details: formValues.details,
            anticipatedRevenueCents: formValues.anticipatedRevenueCents,
            status: formValues.status,
            areaId: formValues.areaId,
            clientId: formValues.clientId,
            packIds: formValues.packIds || [],
        }

        const bookingId = selectedBooking.prefixId || selectedBookingId

        const data = {
            venue_slug: venueSlug,
            id: bookingId,
            booking: filteredBookingData,
        }

        const req = apiBookings.update({ data })
        req.then(() => {
            toast("Booking updated successfully", "success")
            handleCloseModal()
            fetchCalendarData()
        })
        req.catch((res) => {
            const errorMessage = res.body?.errors?.join(", ") || res.message || "Unknown error"
            toast(`Error updating booking. ${errorMessage}`, "danger")
        })
    }

    if (isLoading || !calendarData) {
        return <LoadingWrapper isLoading />
    }

    const getGridTemplateColumns = () => {
        const areaColumnCount = calendarData.areas.length
        return `200px repeat(${areaColumnCount}, minmax(180px, 1fr))`
    }

    const newBookingInitialValues = {
        name: "",
        clientId: "",
        clientAttributes: {
            name: "",
            email: "",
            phone: "",
        },
        anticipatedRevenueCents: 0,
        packIds: [],
        date: prefilledDate || null,
        areaId: prefilledAreaId || "",
    }

    return (
        <>
            <div className="booking-calendar-container">
                <PageTitle title="Booking Calendar">
                    <div className="d-flex gap-2 align-items-center">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={goToToday}
                        >
                            Today
                        </button>
                        <div className="btn-group">
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => navigateMonth('prev')}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                style={{ minWidth: '150px' }}
                                disabled
                            >
                                {currentDate.format('MMMM YYYY')}
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => navigateMonth('next')}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </PageTitle>

                <div className="booking-calendar-grid">
                    <div
                        className="booking-grid"
                        style={{ gridTemplateColumns: getGridTemplateColumns() }}
                    >
                        <div className="booking-header-row">
                            <div className="booking-area-header">Date</div>
                            {calendarData.areas.map((area) => (
                                <div key={area.id} className="booking-date-header">
                                    <div className="booking-date-header-name">{area.name}</div>
                                    {area.capacity && (
                                        <div className="booking-date-header-capacity">Capacity: {area.capacity}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {calendarData.dateRange.map((dateInfo) => {
                            const blocked = dateInfo.isPast || (enabled && !dateInfo.isEnabled)

                            return (
                                <div key={dateInfo.date} className="booking-area-row">
                                    <div className="booking-area-name">
                                        <div className="booking-date-day">{dateInfo.dayName}</div>
                                        <div className="booking-date-full">
                                            {dateInfo.monthName} {dateInfo.dayNumber}
                                        </div>
                                    </div>

                                    {calendarData.areas.map((area) => {
                                        const booking = getBooking(dateInfo.date, area.id)

                                        const canView = !!booking
                                        const canCreate = !blocked && !booking
                                        const isClickable = canView || canCreate

                                        return (
                                          <div
                                            key={`${dateInfo.date}-${area.id}`}
                                            className={`booking-cell ${booking ? "occupied" : "empty"}`}
                                            style={{
                                                opacity: blocked && !canView ? 0.4 : 1,
                                                cursor: isClickable ? "pointer" : "not-allowed",
                                            }}
                                            onClick={() => {
                                                if (canView) return handleEditBooking(booking!.prefixId)
                                                if (canCreate) return handleAddBooking(dateInfo.date, area.prefixId)
                                            }}
                                          >
                                              {booking ? (
                                                <div className="booking-entry">
                                                    <div className="booking-entry-title">{booking.name}</div>
                                                    {booking.clientName && (
                                                      <div className="booking-entry-sponsor">{booking.clientName}</div>
                                                    )}
                                                </div>
                                              ) : (
                                                !blocked && (
                                                  <div className="d-flex align-items-center justify-content-center h-100">
                                                      <Plus size={16} className="opacity-50" />
                                                  </div>
                                                )
                                              )}
                                          </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'new' ? 'New Booking' : 'Edit Booking'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalMode === 'new' ? (
                        <BookingForm
                            onSubmit={handleNewBookingSubmit}
                            initialValues={newBookingInitialValues}
                            onCancel={handleCloseModal}
                        />
                    ) : (
                        <LoadingWrapper isLoading={isSelectedBookingLoading}>
                            <BookingForm
                                onSubmit={handleEditBookingSubmit}
                                initialValues={selectedBooking}
                                isEditing
                                onCancel={handleCloseModal}
                            />
                        </LoadingWrapper>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}

export default BookingCalendar
